import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Plus, X, Trash2, ChevronDown, ChevronRight, UserPlus, AlertCircle, Settings, Users, Search, SortAsc } from 'lucide-react';
import './FunctionalRoleManagement.css';

const FunctionalRoleManagement = () => {
  const { authState } = useOktaAuth();
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [subroles, setSubroles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoles, setExpandedRoles] = useState(new Set());
  const [showAddSubroleModal, setShowAddSubroleModal] = useState(false);
  const [showAssignSubroleModal, setShowAssignSubroleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [subroleFormData, setSubroleFormData] = useState({
    name: '',
    parentRoleId: ''
  });
  const [subroleSearchTerm, setSubroleSearchTerm] = useState('');
  const [showSubroleDropdown, setShowSubroleDropdown] = useState(false);
  const [assignmentFormData, setAssignmentFormData] = useState({
    roleId: '',
    subroleId: '',
    employeeId: ''
  });
  const [roleSubroles, setRoleSubroles] = useState({});
  const [subroleAssignments, setSubroleAssignments] = useState({});
  const [subroleResources, setSubroleResources] = useState({});
  const [resources, setResources] = useState([]);
  const [showAssignResourceModal, setShowAssignResourceModal] = useState(false);
  const [resourceAssignmentFormData, setResourceAssignmentFormData] = useState({
    subroleId: '',
    resourceId: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByTeam, setSortByTeam] = useState(false);
  const [expandedSubroles, setExpandedSubroles] = useState(new Set());

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTeams(),
        fetchRoles(),
        fetchEmployees(),
        fetchSubroles(),
        fetchSubroleAssignments(),
        fetchResources(),
        fetchSubroleResources()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/teams');
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch('/api/roles');
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees?limit=1000');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchSubroles = async () => {
    try {
      const response = await fetch('/api/subroles');
      if (response.ok) {
        const data = await response.json();
        // Group subroles by parent role
        const subrolesByRole = {};
        data.forEach(subrole => {
          if (!subrolesByRole[subrole.parentRoleId]) {
            subrolesByRole[subrole.parentRoleId] = [];
          }
          subrolesByRole[subrole.parentRoleId].push(subrole);
        });
        setRoleSubroles(subrolesByRole);
        setSubroles(data);
      }
    } catch (error) {
      console.error('Error fetching subroles:', error);
      // Initialize empty state if API doesn't exist yet
      setRoleSubroles({});
      setSubroles([]);
    }
  };

  const fetchSubroleAssignments = async () => {
    try {
      const response = await fetch('/api/subrole-assignments');
      if (response.ok) {
        const data = await response.json();
        // Group assignments by subrole
        const assignmentsBySubrole = {};
        data.forEach(assignment => {
          if (!assignmentsBySubrole[assignment.subroleId]) {
            assignmentsBySubrole[assignment.subroleId] = [];
          }
          assignmentsBySubrole[assignment.subroleId].push(assignment);
        });
        setSubroleAssignments(assignmentsBySubrole);
      }
    } catch (error) {
      console.error('Error fetching subrole assignments:', error);
      // Initialize empty state if API doesn't exist yet
      setSubroleAssignments({});
    }
  };

  const fetchResources = async () => {
    try {
      const response = await fetch('/api/resources');
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    }
  };

  const fetchSubroleResources = async () => {
    try {
      const response = await fetch('/api/subrole-resources');
      if (response.ok) {
        const data = await response.json();
        // Group resource assignments by subrole
        const resourcesBySubrole = {};
        data.forEach(assignment => {
          if (!resourcesBySubrole[assignment.subroleId]) {
            resourcesBySubrole[assignment.subroleId] = [];
          }
          resourcesBySubrole[assignment.subroleId].push(assignment);
        });
        setSubroleResources(resourcesBySubrole);
      }
    } catch (error) {
      console.error('Error fetching subrole resources:', error);
      setSubroleResources({});
    }
  };

  const handleAddSubrole = async (e) => {
    e.preventDefault();
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/subroles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...subroleFormData,
          createdBy: currentUser
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newSubrole = result.subrole;
        
        // Update local state
        setSubroles(prev => [...prev, newSubrole]);
        setRoleSubroles(prev => ({
          ...prev,
          [newSubrole.parentRoleId]: [...(prev[newSubrole.parentRoleId] || []), newSubrole]
        }));
        
        setSubroleFormData({ name: '', parentRoleId: '' });
        setShowAddSubroleModal(false);
        setSelectedRole(null);
        
        // Show success notification
        showNotification(`Functional Role "${subroleFormData.name}" created successfully!`, 'success');
      } else {
        const error = await response.json();
        alert('Error adding functional role: ' + error.error);
      }
    } catch (error) {
      console.error('Error adding functional role:', error);
      alert('Error adding functional role. Please try again.');
    }
  };

  const handleAssignSubrole = async (e) => {
    e.preventDefault();
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/subrole-assignments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...assignmentFormData,
          assignedBy: currentUser
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newAssignment = result.assignment;
        
        // Update local state
        setSubroleAssignments(prev => ({
          ...prev,
          [newAssignment.subroleId]: [...(prev[newAssignment.subroleId] || []), newAssignment]
        }));
        
        setAssignmentFormData({ roleId: '', subroleId: '', employeeId: '' });
        setShowAssignSubroleModal(false);
        
        // Show success notification
        const employee = employees.find(emp => emp.id === parseInt(assignmentFormData.employeeId));
        const subrole = subroles.find(sr => sr.id === parseInt(assignmentFormData.subroleId));
        showNotification(`Subrole "${subrole?.name}" assigned to ${employee?.name} successfully!`, 'success');
      } else {
        const error = await response.json();
        alert('Error assigning subrole: ' + error.error);
      }
    } catch (error) {
      console.error('Error assigning subrole:', error);
      alert('Error assigning subrole. Please try again.');
    }
  };

  const handleDeleteSubrole = async (subroleId) => {
    if (!window.confirm('Are you sure you want to delete this subrole? This will also remove all assignments.')) {
      return;
    }

    try {
      const response = await fetch(`/api/subroles/${subroleId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Update local state
        const subroleToDelete = subroles.find(sr => sr.id === subroleId);
        setSubroles(prev => prev.filter(sr => sr.id !== subroleId));
        setRoleSubroles(prev => ({
          ...prev,
          [subroleToDelete.parentRoleId]: prev[subroleToDelete.parentRoleId]?.filter(sr => sr.id !== subroleId) || []
        }));
        setSubroleAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[subroleId];
          return newAssignments;
        });
        
        showNotification('Subrole deleted successfully!', 'success');
      } else {
        alert('Error deleting subrole');
      }
    } catch (error) {
      console.error('Error deleting subrole:', error);
      alert('Error deleting subrole');
    }
  };

  const handleRemoveAssignment = async (assignmentId, subroleId) => {
    if (!window.confirm('Are you sure you want to remove this subrole assignment?')) {
      return;
    }

    try {
      const response = await fetch(`/api/subrole-assignments/${assignmentId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Update local state
        setSubroleAssignments(prev => ({
          ...prev,
          [subroleId]: prev[subroleId]?.filter(assignment => assignment.id !== assignmentId) || []
        }));
        
        showNotification('Assignment removed successfully!', 'success');
      } else {
        alert('Error removing assignment');
      }
    } catch (error) {
      console.error('Error removing assignment:', error);
      alert('Error removing assignment');
    }
  };

  const toggleRoleExpansion = (roleId) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId);
    } else {
      newExpanded.add(roleId);
    }
    setExpandedRoles(newExpanded);
  };

  const toggleSubroleExpansion = (subroleId) => {
    const newExpanded = new Set(expandedSubroles);
    if (newExpanded.has(subroleId)) {
      newExpanded.delete(subroleId);
    } else {
      newExpanded.add(subroleId);
    }
    setExpandedSubroles(newExpanded);
  };

  const openAddSubroleModal = (roleId = null) => {
    setSelectedRole(roleId);
    setSubroleFormData({ name: '', parentRoleId: roleId || '' });
    setSubroleSearchTerm('');
    setShowSubroleDropdown(false);
    setShowAddSubroleModal(true);
  };

  const openAssignSubroleModal = () => {
    setAssignmentFormData({ roleId: '', subroleId: '', employeeId: '' });
    setShowAssignSubroleModal(true);
  };

  const openAssignResourceModal = (subroleId = null) => {
    setResourceAssignmentFormData({ subroleId: subroleId || '', resourceId: '' });
    setShowAssignResourceModal(true);
  };

  const handleAssignResource = async (e) => {
    e.preventDefault();
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/subrole-resources', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...resourceAssignmentFormData,
          assignedBy: currentUser
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newAssignment = result.assignment;
        
        // Update local state
        setSubroleResources(prev => ({
          ...prev,
          [newAssignment.subroleId]: [...(prev[newAssignment.subroleId] || []), newAssignment]
        }));
        
        setResourceAssignmentFormData({ subroleId: '', resourceId: '' });
        setShowAssignResourceModal(false);
        
        // Show success notification
        const resource = resources.find(res => res.ResourceID === parseInt(resourceAssignmentFormData.resourceId));
        const subrole = subroles.find(sr => sr.id === parseInt(resourceAssignmentFormData.subroleId));
        showNotification(`Resource "${resource?.AccessResource}" assigned to functional role "${subrole?.name}" successfully!`, 'success');
      } else {
        const error = await response.json();
        alert('Error assigning resource: ' + error.error);
      }
    } catch (error) {
      console.error('Error assigning resource:', error);
      alert('Error assigning resource. Please try again.');
    }
  };

  const handleRemoveResourceAssignment = async (assignmentId, subroleId) => {
    if (!window.confirm('Are you sure you want to remove this resource assignment?')) {
      return;
    }

    try {
      const response = await fetch(`/api/subrole-resources/${assignmentId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Update local state
        setSubroleResources(prev => ({
          ...prev,
          [subroleId]: prev[subroleId]?.filter(assignment => assignment.id !== assignmentId) || []
        }));
        
        showNotification('Resource assignment removed successfully!', 'success');
      } else {
        alert('Error removing resource assignment');
      }
    } catch (error) {
      console.error('Error removing resource assignment:', error);
      alert('Error removing resource assignment');
    }
  };

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.className = `success-notification ${type === 'success' ? '' : 'error-notification'}`;
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">${type === 'success' ? '✓' : '⚠'}</span>
        <span class="notification-message">${message}</span>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  // Get subroles for a specific role when role changes in assignment form
  const getSubrolesForRole = (roleId) => {
    return roleSubroles[roleId] || [];
  };

  // Get employees that have a specific role
  const getEmployeesWithRole = (roleId) => {
    const role = roles.find(r => r.RoleID === parseInt(roleId));
    if (!role) return [];
    
    return employees.filter(emp => emp.role === role.Title);
  };

  // Filter and sort functional roles based on search term
  const getFilteredAndSortedFunctionalRoles = () => {
    let filteredSubroles = subroles;

    // Filter by search term
    if (searchTerm.trim()) {
      filteredSubroles = subroles.filter(subrole => 
        subrole.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subrole.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by name
    if (sortByTeam) {
      filteredSubroles = [...filteredSubroles].sort((a, b) => {
        const roleA = roles.find(r => r.RoleID === a.parentRoleId);
        const roleB = roles.find(r => r.RoleID === b.parentRoleId);
        const teamA = roleA?.TeamName || '';
        const teamB = roleB?.TeamName || '';
        
        if (teamA === teamB) {
          return a.name.localeCompare(b.name);
        }
        return teamA.localeCompare(teamB);
      });
    }

    return filteredSubroles;
  };

  // Get filtered existing subroles for dropdown
  const getFilteredExistingSubroles = () => {
    if (!subroleSearchTerm.trim()) return subroles;
    return subroles.filter(subrole => 
      subrole.name.toLowerCase().includes(subroleSearchTerm.toLowerCase())
    );
  };

  // Handle subrole selection from dropdown
  const handleSubroleSelect = (subroleName) => {
    setSubroleFormData(prev => ({ ...prev, name: subroleName }));
    setSubroleSearchTerm(subroleName);
    setShowSubroleDropdown(false);
  };

  // Handle subrole search input
  const handleSubroleSearchChange = (value) => {
    setSubroleSearchTerm(value);
    setSubroleFormData(prev => ({ ...prev, name: value }));
    setShowSubroleDropdown(value.length > 0);
  };

  if (loading) {
    return (
      <div className="functional-role-management">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div className="loading-spinner"></div>
          <div>Loading functional roles data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="functional-role-management">
      <div className="functional-header">
        <div>
          <h2>Functional Groups</h2>
          <p>Group resources by working function.</p>
        </div>
        <div className="header-actions">
          <button 
            className="add-subrole-btn"
            onClick={() => openAddSubroleModal()}
          >
            <Plus size={16} />
            Add Functional Group
          </button>
          <button 
            className="assign-subrole-btn"
            onClick={openAssignSubroleModal}
          >
            <UserPlus size={16} />
            Assign Functional Role
          </button>
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className="controls-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search roles, teams, or functional roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="sort-controls">
          <button
            className={`sort-btn ${sortByTeam ? 'active' : ''}`}
            onClick={() => setSortByTeam(!sortByTeam)}
            title="Sort by teams"
          >
            <SortAsc size={16} />
            Sort by Teams
          </button>
        </div>
      </div>

      {/* Add Subrole Modal */}
      {showAddSubroleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Functional Role</h3>
              <button className="close-btn" onClick={() => setShowAddSubroleModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddSubrole} className="modal-form">
              <div className="form-group">
                <label htmlFor="parentRole">Parent Role *</label>
                <select
                  id="parentRole"
                  value={subroleFormData.parentRoleId}
                  onChange={e => setSubroleFormData(prev => ({ ...prev, parentRoleId: e.target.value }))}
                  required
                >
                  <option value="">Select Parent Role</option>
                  {roles.map(role => (
                    <option key={role.RoleID} value={role.RoleID}>
                      {role.Title} ({role.TeamName})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subroleName">Functional Role Name *</label>
                <div className="searchable-dropdown-container">
                  <input
                    type="text"
                    id="subroleName"
                    value={subroleSearchTerm}
                    onChange={e => handleSubroleSearchChange(e.target.value)}
                    onFocus={() => setShowSubroleDropdown(true)}
                    placeholder="Search existing functional roles or type new name..."
                    required
                  />
                  {showSubroleDropdown && (
                    <div className="dropdown-list">
                      {getFilteredExistingSubroles().length > 0 ? (
                        getFilteredExistingSubroles().map(subrole => (
                          <div
                            key={subrole.id}
                            className="dropdown-item"
                            onClick={() => handleSubroleSelect(subrole.name)}
                          >
                            {subrole.name}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item no-results">
                          No existing functional roles found. Type to create new.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddSubroleModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Add Functional Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Subrole Modal */}
      {showAssignSubroleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign Functional Role to Employee</h3>
              <button className="close-btn" onClick={() => setShowAssignSubroleModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAssignSubrole} className="modal-form">
              <div className="form-group">
                <label htmlFor="assignRole">Role *</label>
                <select
                  id="assignRole"
                  value={assignmentFormData.roleId}
                  onChange={e => setAssignmentFormData(prev => ({ 
                    ...prev, 
                    roleId: e.target.value,
                    subroleId: '', // Reset subrole when role changes
                    employeeId: '' // Reset employee when role changes
                  }))}
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map(role => (
                    <option key={role.RoleID} value={role.RoleID}>
                      {role.Title} ({role.TeamName})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="assignSubrole">Functional Role *</label>
                <select
                  id="assignSubrole"
                  value={assignmentFormData.subroleId}
                  onChange={e => setAssignmentFormData(prev => ({ ...prev, subroleId: e.target.value }))}
                  required
                  disabled={!assignmentFormData.roleId}
                >
                  <option value="">
                    {assignmentFormData.roleId ? 'Select Functional Role' : 'Select Role first'}
                  </option>
                  {getSubrolesForRole(assignmentFormData.roleId).map(subrole => (
                    <option key={subrole.id} value={subrole.id}>
                      {subrole.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="assignEmployee">Employee *</label>
                <select
                  id="assignEmployee"
                  value={assignmentFormData.employeeId}
                  onChange={e => setAssignmentFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                  required
                  disabled={!assignmentFormData.roleId}
                >
                  <option value="">
                    {assignmentFormData.roleId ? 'Select Employee' : 'Select Role first'}
                  </option>
                  {getEmployeesWithRole(assignmentFormData.roleId).map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name} ({employee.department})
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAssignSubroleModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Assign Functional Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Resource Modal */}
      {showAssignResourceModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign Resource to Functional Role</h3>
              <button className="close-btn" onClick={() => setShowAssignResourceModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAssignResource} className="modal-form">
              <div className="form-group">
                <label htmlFor="assignResourceSubrole">Functional Role *</label>
                <select
                  id="assignResourceSubrole"
                  value={resourceAssignmentFormData.subroleId}
                  onChange={e => setResourceAssignmentFormData(prev => ({ ...prev, subroleId: e.target.value }))}
                  required
                >
                  <option value="">Select Functional Role</option>
                  {subroles.map(subrole => (
                    <option key={subrole.id} value={subrole.id}>
                      {subrole.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="assignResourceResource">Resource *</label>
                <select
                  id="assignResourceResource"
                  value={resourceAssignmentFormData.resourceId}
                  onChange={e => setResourceAssignmentFormData(prev => ({ ...prev, resourceId: e.target.value }))}
                  required
                >
                  <option value="">Select Resource</option>
                  {resources.map(resource => (
                    <option key={resource.ResourceID} value={resource.ResourceID}>
                      {resource.AccessResource} ({resource.TargetSystem} - {resource.AccessEnvironment})
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAssignResourceModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Assign Resource
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Functional Roles List */}
      <div className="functional-roles-container">
        {subroles.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} className="empty-icon" />
            <h3>No functional roles found</h3>
            <p>Create functional roles to assign specialized responsibilities to employees.</p>
          </div>
        ) : (
          <div className="functional-roles-list">
            {getFilteredAndSortedFunctionalRoles().map(subrole => {
              const assignments = subroleAssignments[subrole.id] || [];
              const resourceAssignments = subroleResources[subrole.id] || [];
              const parentRole = roles.find(r => r.RoleID === subrole.parentRoleId);
              
              const isExpanded = expandedSubroles.has(subrole.id);
              const hasContent = assignments.length > 0 || resourceAssignments.length > 0;
              
              return (
                <div key={subrole.id} className={`functional-role-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="functional-role-header">
                    <button
                      className="functional-role-expand-btn"
                      onClick={() => toggleSubroleExpansion(subrole.id)}
                      disabled={!hasContent}
                      style={{ flex: 1, border: 'none', background: 'transparent', textAlign: 'left', cursor: hasContent ? 'pointer' : 'default' }}
                    >
                      <div className="functional-role-info">
                        <div className="functional-role-title-row">
                          {hasContent && (
                            <ChevronRight 
                              size={16} 
                              className={`chevron ${isExpanded ? 'rotated' : ''}`}
                            />
                          )}
                          <h3 className="functional-role-name">{subrole.name}</h3>
                        </div>
                        {subrole.description && (
                          <p className="functional-role-description">{subrole.description}</p>
                        )}
                        <div className="parent-role-info">
                          <span className="parent-role-label">Parent Role:</span>
                          <span className="parent-role-name">{parentRole?.Title}</span>
                          <span className="parent-team-badge">{parentRole?.TeamName}</span>
                        </div>
                      </div>
                    </button>
                    <div className="functional-role-actions">
                      <span className="assignment-count">
                        {assignments.length} assignment{assignments.length !== 1 ? 's' : ''} | {resourceAssignments.length} resource{resourceAssignments.length !== 1 ? 's' : ''}
                      </span>
                      <button
                        className="assign-resource-btn"
                        onClick={() => openAssignResourceModal(subrole.id)}
                        title="Assign resource to this functional role"
                      >
                        <Settings size={14} />
                      </button>
                      <button
                        className="delete-subrole-btn"
                        onClick={() => handleDeleteSubrole(subrole.id)}
                        title="Delete functional role"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  {isExpanded && hasContent && (
                    <div className="functional-role-content">
                      {assignments.length > 0 && (
                        <div className="assignments-section">
                          <h4>Assigned Employees:</h4>
                          <div className="assignments-grid">
                            {assignments.map(assignment => {
                              const employee = employees.find(emp => emp.id === assignment.employeeId);
                              return (
                                <div key={assignment.id} className="assignment-item">
                                  <div className="employee-info">
                                    <span className="employee-name">{employee?.name || 'Unknown Employee'}</span>
                                    <span className="employee-department">{employee?.department}</span>
                                  </div>
                                  <button
                                    className="remove-assignment-btn"
                                    onClick={() => handleRemoveAssignment(assignment.id, subrole.id)}
                                    title="Remove assignment"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {resourceAssignments.length > 0 && (
                        <div className="resources-section">
                          <h4>Assigned Resources:</h4>
                          <div className="resources-compact-list">
                            {resourceAssignments.map(resourceAssignment => (
                              <div key={resourceAssignment.id} className="resource-compact-item">
                                <div className="resource-compact-info">
                                  <span className="resource-name">{resourceAssignment.resourceName}</span>
                                  <span className="resource-system">{resourceAssignment.TargetSystem}</span>
                                  <span className={`resource-risk risk-${resourceAssignment.DataRisk?.toLowerCase()}`}>
                                    {resourceAssignment.DataRisk}
                                  </span>
                                </div>
                                <button
                                  className="remove-resource-btn"
                                  onClick={() => handleRemoveResourceAssignment(resourceAssignment.id, subrole.id)}
                                  title="Remove resource assignment"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FunctionalRoleManagement;
